import { formatMicro, type Micro, microFromString } from "./exact-numbers.js";
import { addTroops, EMPTY_TROOPS } from "./logistics-math.js";
import { startTravel } from "./logistics-state.js";
import type { LogisticsState, ReinforcementState, TroopComposition } from "./logistics-types.js";
import { UNIT_IDS } from "./logistics-types.js";
import { restartWaveTimer } from "./wave-timing.js";

// Allocate exact micro-units through a persistent 100-slot cycle, independent of
// declaration order and render/tick partitioning. Residual slots belong to reserves.
export function routeProduction(state: ReinforcementState, produced: TroopComposition) {
  const gathered = { ...state.gathered };
  const cursors = { ...state.cursors };
  const home = { ...EMPTY_TROOPS };
  for (const unit of UNIT_IDS) {
    const slots = Object.entries(state.shares[unit])
      .sort(([a], [b]) => (a < b ? -1 : 1))
      .flatMap(([id, percent]) => Array<string>(percent).fill(id));
    while (slots.length < 100) slots.push("");
    const amount = microFromString(produced[unit]);
    const counts: Record<string, bigint> = {};
    const whole = amount / 100n;
    for (const id of slots) counts[id] = (counts[id] ?? 0n) + whole;
    for (let i = 0; i < Number(amount % 100n); i++) {
      const id = slots[(cursors[unit] + i) % 100] as string;
      counts[id] = (counts[id] as bigint) + 1n;
    }
    cursors[unit] = (cursors[unit] + Number(amount % 100n)) % 100;
    for (const [id, count] of Object.entries(counts)) {
      if (id === "") home[unit] = formatMicro(count as Micro);
      else
        gathered[id] = {
          ...(gathered[id] ?? EMPTY_TROOPS),
          [unit]: formatMicro((microFromString(gathered[id]?.[unit] ?? "0") + count) as Micro),
        };
    }
  }
  return { state: { ...state, gathered, cursors }, home };
}

export function launchGathered(
  state: LogisticsState,
  id: string,
  now: number,
  multiplier: string,
  step: number,
): LogisticsState {
  const reinforcement = state.reinforcement;
  const troops = reinforcement?.gathered[id] ?? EMPTY_TROOPS;
  if (!reinforcement || UNIT_IDS.every((unit) => troops[unit] === "0"))
    throw new TypeError("No troops gathered for this front");
  const moved = startTravel(state, "home", id, troops, now, multiplier, step) as LogisticsState;
  return {
    ...moved,
    reinforcement: {
      ...restartWaveTimer(reinforcement, id, now),
      gathered: { ...reinforcement.gathered, [id]: EMPTY_TROOPS },
    },
  };
}

export function dispatchReinforcements(
  state: LogisticsState,
  now: number,
  multiplier: string,
  step: number,
  valid: ReadonlySet<string>,
) {
  let current = state;
  let returned = EMPTY_TROOPS;
  if (!current.reinforcement) return { state: current, returned };
  const reinforcement = current.reinforcement;
  const gathered = { ...reinforcement.gathered };
  for (const [id, troops] of Object.entries(gathered)) {
    if (valid.has(id)) continue;
    returned = addTroops(returned, troops);
    delete gathered[id];
  }
  current = {
    ...current,
    reinforcement: {
      ...reinforcement,
      gathered,
      ...(reinforcement.timers
        ? {
            timers: Object.fromEntries(
              Object.entries(reinforcement.timers).filter(([id]) => valid.has(id)),
            ),
          }
        : {}),
      shares: Object.fromEntries(
        UNIT_IDS.map((unit) => [
          unit,
          Object.fromEntries(
            Object.entries(reinforcement.shares[unit]).filter(([id]) => valid.has(id)),
          ),
        ]),
      ) as ReinforcementState["shares"],
    },
  };
  const legacyDue = now >= current.nextAutoReinforceAtMs;
  if (legacyDue)
    current = { ...current, nextAutoReinforceAtMs: (Math.floor(now / 5000) + 1) * 5000 };
  for (const id of [
    ...new Set([...Object.keys(gathered), ...Object.keys(reinforcement.timers ?? {})]),
  ].sort()) {
    const troops = gathered[id] ?? EMPTY_TROOPS;
    const timer = reinforcement.timers?.[id];
    if (
      !valid.has(id) ||
      reinforcement.modes[id] !== "continuous" ||
      !(timer ? now >= timer.nextAtMs : legacyDue)
    )
      continue;
    if (UNIT_IDS.some((unit) => microFromString(troops[unit]) > 0n))
      current = launchGathered(current, id, now, multiplier, step);
    else if (timer)
      current = {
        ...current,
        reinforcement: restartWaveTimer(current.reinforcement as ReinforcementState, id, now),
      };
  }
  return { state: current, returned };
}
